<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/yup'
import { emailSchema } from '@/types'
import DashboardFormField from '@/components/dashboard/ui/DashboardFormField.vue'
import { useRequestEmailVerification } from '@/composables/useRequestEmailVerification'
import {
  EMAIL_ACCOUNT_ERROR_CODES,
  EmailVerificationRequestError,
} from '@/helpers/emailAccountErrors'

const { t } = useI18n()
const { requestVerification, isSubmitting } = useRequestEmailVerification()

const validationSchema = toTypedSchema(emailSchema)
const { handleSubmit, errors, defineField, resetForm } = useForm({
  validationSchema,
})
const [email] = defineField('email', {
  validateOnModelUpdate: false,
})

const isFormOpen = ref(false)
const apiError = ref('')
const showContactSupportError = ref(false)
const successEmail = ref('')
const devVerificationUrl = ref('')
const showSuccess = ref(false)
const addLinkRef = ref<HTMLButtonElement | null>(null)
const emailInputRef = ref<HTMLInputElement | null>(null)

const isMockApi = import.meta.env.MODE === 'mockapi'

const emailFieldError = computed(() => {
  const validationError = errors.value.email
  return validationError || apiError.value
})

watch(email, () => {
  apiError.value = ''
  showContactSupportError.value = false
})

const openForm = async () => {
  showSuccess.value = false
  devVerificationUrl.value = ''
  apiError.value = ''
  showContactSupportError.value = false
  resetForm()
  isFormOpen.value = true
  await nextTick()
  emailInputRef.value?.focus()
}

const closeForm = async () => {
  isFormOpen.value = false
  apiError.value = ''
  showContactSupportError.value = false
  resetForm()
  await nextTick()
  addLinkRef.value?.focus()
}

const dismissSuccess = () => {
  showSuccess.value = false
  successEmail.value = ''
  devVerificationUrl.value = ''
}

const submit = handleSubmit(async (values) => {
  apiError.value = ''
  showContactSupportError.value = false

  try {
    const result = await requestVerification(values.email)
    successEmail.value = result.email
    devVerificationUrl.value = result.devVerificationUrl ?? ''
    showSuccess.value = true
    isFormOpen.value = false
    resetForm()
    await nextTick()
    addLinkRef.value?.focus()
  } catch (error) {
    if (
      error instanceof EmailVerificationRequestError &&
      error.code === EMAIL_ACCOUNT_ERROR_CODES.EMAIL_ALREADY_ON_SWIFTAID
    ) {
      showContactSupportError.value = true
    } else if (
      error instanceof EmailVerificationRequestError &&
      error.code === EMAIL_ACCOUNT_ERROR_CODES.ALREADY_LINKED
    ) {
      apiError.value = t('Dashboard.Status.Emails.AddForm.AlreadyLinked')
    } else {
      apiError.value = t('Dashboard.Status.Emails.AddForm.Failure')
    }
    await nextTick()
    emailInputRef.value?.focus()
  }
})
</script>

<template>
  <div class="sa-matching-emails__add">
    <div
      v-if="showSuccess"
      class="sa-matching-emails__success"
      role="status"
      aria-live="polite"
      data-testid="email-request-success-alert"
    >
      <i18n-t
        keypath="Dashboard.Status.Emails.AddForm.SuccessPara1"
        tag="p"
        class="sa-matching-emails__success-lead"
        scope="global"
      >
        <template #email>
          <strong>{{ successEmail }}</strong>
        </template>
      </i18n-t>
      <p class="sa-matching-emails__success-note mb-0">
        {{ $t('Dashboard.Status.Emails.AddForm.SuccessPara2') }}
      </p>
      <p
        v-if="isMockApi && devVerificationUrl"
        class="sa-matching-emails__dev-link"
      >
        <span class="sa-matching-emails__dev-link-label">
          {{ $t('Dashboard.Status.Emails.AddForm.DevLinkLabel') }}
        </span>
        <a :href="devVerificationUrl" class="sa-matching-emails__text-link">
          {{ $t('Dashboard.Status.Emails.AddForm.DevLinkCta') }}
        </a>
      </p>
      <button
        type="button"
        class="sa-matching-emails__text-link sa-matching-emails__success-dismiss"
        :aria-label="$t('Dashboard.Status.Emails.AddForm.SuccessDismiss')"
        @click="dismissSuccess"
      >
        {{ $t('Buttons.Close') }}
      </button>
    </div>

    <div
      v-if="isFormOpen"
      id="matching-add-email-form"
      class="sa-matching-emails__form"
      data-testid="add-email-account-form"
    >
      <h3 id="matching-add-email-title" class="sa-matching-emails__form-title">
        {{ $t('Dashboard.Status.Emails.AddForm.Title') }}
      </h3>
      <p id="matching-add-email-intro" class="sa-matching-emails__form-intro">
        {{ $t('Dashboard.Status.Emails.AddForm.Intro') }}
      </p>

      <form
        class="sa-matching-emails__form-inner"
        novalidate
        aria-labelledby="matching-add-email-title"
        aria-describedby="matching-add-email-intro"
        @submit.prevent="submit"
      >
        <DashboardFormField
          :label="$t('Dashboard.Status.Emails.AddForm.Label')"
          label-for="matching-add-email"
          :error="emailFieldError"
        >
          <template #default="{ errorId, hasError }">
            <input
              id="matching-add-email"
              ref="emailInputRef"
              v-model="email"
              class="sa-input"
              type="email"
              name="email"
              inputmode="email"
              spellcheck="false"
              autocomplete="email"
              enterkeyhint="done"
              :placeholder="$t('Dashboard.Status.Emails.AddForm.Placeholder')"
              :aria-invalid="hasError || undefined"
              :aria-describedby="hasError ? errorId : undefined"
            />
          </template>
        </DashboardFormField>

        <div
          v-if="showContactSupportError"
          class="sa-matching-emails__request-error-notice"
          role="alert"
          aria-live="assertive"
        >
          <p class="sa-matching-emails__request-error-lead mb-0">
            {{ $t('Dashboard.Status.Emails.AddForm.RequestErrorLead') }}
          </p>
          <p class="sa-matching-emails__request-error-support mb-0">
            {{ $t('Dashboard.Status.Emails.AddForm.RequestErrorSupport') }}
            <a
              class="sa-matching-emails__text-link"
              href="mailto:support@swiftaid.co.uk"
              :aria-label="$t('Dashboard.Status.Emails.AddForm.SupportLinkAria')"
            >
              {{ $t('Dashboard.Status.Emails.AddForm.SupportEmail') }}
            </a>
          </p>
        </div>

        <div class="sa-dashboard-form__action-row sa-matching-emails__form-actions">
          <button
            type="submit"
            class="sa-btn-primary"
            :disabled="isSubmitting"
            :aria-busy="isSubmitting"
            :aria-label="$t('Dashboard.Status.Emails.AddForm.Submit')"
          >
            <span v-if="isSubmitting" class="spinner sa-btn-spinner" aria-hidden="true" />
            <span>{{ $t('Dashboard.Status.Emails.AddForm.Submit') }}</span>
          </button>
          <button
            type="button"
            class="sa-btn-secondary"
            :disabled="isSubmitting"
            @click="closeForm"
          >
            {{ $t('Dashboard.Status.Emails.AddForm.Cancel') }}
          </button>
        </div>
      </form>
    </div>

    <button
      v-else
      ref="addLinkRef"
      type="button"
      class="sa-matching-emails__text-link sa-matching-emails__add-link"
      data-testid="add-email-account-button"
      :aria-expanded="isFormOpen"
      aria-controls="matching-add-email-form"
      @click="openForm"
    >
      {{ $t('Dashboard.Status.Emails.Add') }}
    </button>
  </div>
</template>
